import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box, Stack, Typography, TextField, MenuItem, Button, Alert, Paper, Tabs, Tab, Chip,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  IconButton, Tooltip, CircularProgress, Autocomplete, Dialog, DialogTitle,
  DialogContent, DialogActions, Divider,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/EditOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import { BRAND } from "../styles/theme";
import { StatusChip, fmtMoney, fmtDateTime } from "../components/collectors/chips";
import ConfirmDialog from "../components/collectors/ConfirmDialog";
import { CollectorSummaryRow, getCollectorSummary } from "../services/collectorStats";
import { Collector, setConfigValue, removeConfigKey } from "../services/collectors";
import {
  getSplits, getSplitProjection, SplitRow, SplitProjection, SPLIT_PARTNERS,
  getPartners, addPartner, removePartner,
} from "../services/splits";
import { downloadPartnerReport, downloadAllPartnerReports, partnerReportFilename } from "../lib/splitsReport";

const squareInput = { "& .MuiOutlinedInput-root": { borderRadius: 1 } };
const pct = (n: number | null | undefined) => (n == null ? "—" : `${(n * 100).toFixed(3)}%`);

function toCollector(r: CollectorSummaryRow): Collector {
  return {
    id: r.id, paymentsProcessor: r.payments_processor, closeId: r.close_id,
    active: r.active, settlementEnabled: r.settlement, doingBusinessAs: "",
  };
}

interface Joined extends SplitRow { processor: string; hasCollector: boolean; }

export default function CollectorSplits() {
  const [tab, setTab] = useState<"config" | "projection" | "partners">("config");
  return (
    <Box>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}>
        <Tab value="config" label="Configuración" />
        <Tab value="projection" label="Proyección" />
        <Tab value="partners" label="Partners" />
      </Tabs>
      {tab === "config" ? <ConfigView /> : tab === "projection" ? <ProjectionView /> : <PartnersView />}
    </Box>
  );
}

/* ------------------------------- CRUD ------------------------------- */
function ConfigView() {
  const [summary, setSummary] = useState<CollectorSummaryRow[]>([]);
  const [splits, setSplits] = useState<SplitRow[]>([]);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [partners, setPartners] = useState<string[]>([...SPLIT_PARTNERS]);
  const [edit, setEdit] = useState<{ collector: string; partner: string; rate: string; isNew: boolean } | null>(null);
  const [del, setDel] = useState<Joined | null>(null);

  const byId = useMemo(() => new Map(summary.map((r) => [r.id, r])), [summary]);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const [sum, sp, pt] = await Promise.all([
        getCollectorSummary(), getSplits(), getPartners().catch(() => [...SPLIT_PARTNERS])]);
      setSummary(sum.items); setSplits(sp.items); setUpdatedAt(sp.generated_at);
      setPartners(pt.length ? pt : [...SPLIT_PARTNERS]);
    } catch (e: any) { setErr(String(e?.message ?? e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const rows: Joined[] = useMemo(() => {
    const s = q.trim().toLowerCase();
    return splits
      .map((r) => ({ ...r, processor: byId.get(r.collector)?.payments_processor ?? "—", hasCollector: byId.has(r.collector) }))
      .filter((r) => !s || r.collector.toLowerCase().includes(s) || (r.split_to ?? "").toLowerCase().includes(s));
  }, [splits, byId, q]);

  const collectorObj = (id: string) => { const r = byId.get(id); return r ? toCollector(r) : null; };

  async function saveEdit() {
    if (!edit) return;
    const c = collectorObj(edit.collector);
    if (!c) throw new Error(`No encuentro el collector ${edit.collector} en el catálogo`);
    const rate = edit.rate.trim();
    // slot #1: partner + rate. Ambos requeridos para un split completo.
    await setConfigValue(c, "split_to", edit.partner);
    await setConfigValue(c, "split_rate", rate);
    setEdit(null); setOk(`Split guardado para ${edit.collector}`); await load();
  }

  async function doDelete() {
    if (!del) return;
    const c = collectorObj(del.collector);
    if (!c) throw new Error(`No encuentro el collector ${del.collector}`);
    if (del.split_to != null) await removeConfigKey(c, "split_to");
    if (del.split_rate != null) await removeConfigKey(c, "split_rate");
    setDel(null); setOk(`Split eliminado de ${del.collector}`); await load();
  }

  const withoutSplit = useMemo(() => {
    const have = new Set(splits.map((s) => s.collector));
    return summary.filter((r) => !have.has(r.id)).map((r) => r.id).sort();
  }, [summary, splits]);

  return (
    <>
      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ md: "center" }} sx={{ mb: 2 }}>
        <TextField size="small" label="Buscar" placeholder="Collector o partner…" value={q} onChange={(e) => setQ(e.target.value)} sx={{ width: 300 }} />
        <Box sx={{ flex: 1 }} />
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEdit({ collector: "", partner: "", rate: "", isNew: true })} sx={{ height: 40 }}>
          Agregar split
        </Button>
      </Stack>

      {err && <Alert severity="error" onClose={() => setErr(null)} sx={{ mb: 2 }}>{err}</Alert>}
      {ok && <Alert severity="success" onClose={() => setOk(null)} sx={{ mb: 2 }}>{ok}</Alert>}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead sx={{ bgcolor: BRAND.primaryLight }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Collector</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Partner</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Rate</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Estado</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4, color: "text.secondary" }}>No hay splits configurados{q ? " para la búsqueda" : ""}.</TableCell></TableRow>
            ) : rows.map((r) => (
              <TableRow key={r.collector} hover>
                <TableCell sx={{ fontWeight: 600 }}>{r.collector}
                  <Typography variant="caption" sx={{ display: "block", color: "text.secondary" }}>{r.processor}</Typography>
                </TableCell>
                <TableCell>{r.split_to ? <Chip size="small" label={r.split_to} /> : <span style={{ color: "#888" }}>sin partner</span>}</TableCell>
                <TableCell sx={{ fontFamily: "monospace" }}>{r.split_rate != null ? pct(r.split_rate) : <span style={{ color: "#888" }}>—</span>}</TableCell>
                <TableCell>{r.complete ? <StatusChip kind="ok" label="Completo" /> : <StatusChip kind="warn" label="Incompleto" />}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                  <Tooltip title="Editar"><span><IconButton size="small" disabled={!r.hasCollector}
                    onClick={() => setEdit({ collector: r.collector, partner: r.split_to ?? "", rate: r.split_rate != null ? String(r.split_rate) : "", isNew: false })}>
                    <EditIcon fontSize="small" /></IconButton></span></Tooltip>
                  <Tooltip title="Eliminar split"><span><IconButton size="small" color="error" disabled={!r.hasCollector} onClick={() => setDel(r)}>
                    <DeleteIcon fontSize="small" /></IconButton></span></Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, py: 1, borderTop: 1, borderColor: "divider" }}>
          <Typography variant="caption" color="text.secondary">
            {rows.length} con split{updatedAt ? ` · leído ${fmtDateTime(updatedAt)}` : ""}
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Tooltip title="Refrescar"><span><IconButton size="small" onClick={load} disabled={loading}><RefreshIcon /></IconButton></span></Tooltip>
        </Stack>
      </TableContainer>

      <SplitDialog edit={edit} candidates={withoutSplit} partners={partners} onClose={() => setEdit(null)} onSave={saveEdit}
        onChange={(patch) => setEdit((e) => (e ? { ...e, ...patch } : e))} />

      <ConfirmDialog open={!!del} onClose={() => setDel(null)} onConfirm={doDelete} danger
        title="Eliminar split" okLabel="Eliminar" busyLabel="Eliminando…">
        Se eliminan las claves <code>split_to</code> y <code>split_rate</code> de <b>{del?.collector}</b>. El collector deja de tener split.
      </ConfirmDialog>
    </>
  );
}

function SplitDialog({ edit, candidates, partners, onClose, onSave, onChange }: {
  edit: { collector: string; partner: string; rate: string; isNew: boolean } | null;
  candidates: string[];
  partners: string[];
  onClose: () => void;
  onSave: () => Promise<void>;
  onChange: (patch: Partial<{ collector: string; partner: string; rate: string }>) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { if (edit) setErr(null); }, [edit]);
  if (!edit) return null;

  const rateNum = Number(edit.rate);
  const rateOk = edit.rate.trim() !== "" && !isNaN(rateNum) && rateNum > 0 && rateNum <= 1;
  const valid = !!edit.collector && !!edit.partner && rateOk;

  async function go() {
    setBusy(true); setErr(null);
    try { await onSave(); }
    catch (e: any) { setErr(String(e?.message ?? e)); }
    finally { setBusy(false); }
  }

  return (
    <Dialog open={!!edit} onClose={() => !busy && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>{edit.isNew ? "Agregar split" : "Editar split"}
        <Typography variant="body2" color="text.secondary">
          {edit.isNew ? "Elegí el collector y el partner + tasa a compartir" : edit.collector}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          {edit.isNew && (
            <Autocomplete options={candidates} value={edit.collector || null} disabled={busy}
              onChange={(_, v) => onChange({ collector: v ?? "" })}
              renderInput={(p) => <TextField {...p} label="Collector" sx={squareInput} />} />
          )}

          {/* Split #1 — funcional */}
          <Paper variant="outlined" sx={{ p: 1.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>Split 1</Typography>
            <Stack spacing={1.5} mt={1}>
              <TextField select fullWidth label="Partner" value={edit.partner} disabled={busy} onChange={(e) => onChange({ partner: e.target.value })} sx={squareInput}>
                {partners.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
              </TextField>
              <TextField fullWidth label="Rate" type="number" value={edit.rate} disabled={busy} onChange={(e) => onChange({ rate: e.target.value })}
                inputProps={{ step: "0.00001", min: 0, max: 1 }} error={edit.rate !== "" && !rateOk}
                helperText={rateOk ? `= ${pct(rateNum)}` : "Fracción 0–1 (0.05 = 5%)"} sx={squareInput} />
            </Stack>
          </Paper>

          {/* Splits #2 y #3 — preparados, deshabilitados (falta soporte multi-partner en DB) */}
          {[2, 3].map((n) => (
            <Paper key={n} variant="outlined" sx={{ p: 1.5, opacity: 0.6 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>Split {n}</Typography>
                <Chip size="small" label="Próximamente" color="default" />
              </Stack>
              <Stack spacing={1.5} mt={1}>
                <TextField select fullWidth label="Partner" value="" disabled sx={squareInput}><MenuItem value="" /></TextField>
                <TextField fullWidth label="Rate" disabled sx={squareInput} />
              </Stack>
            </Paper>
          ))}
          <Typography variant="caption" color="text.secondary">
            Multi-partner (hasta 3) requiere un cambio de base de datos aún pendiente. Por ahora se guarda un solo split.
          </Typography>

          {err && <Alert severity="error">{err}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancelar</Button>
        <Button variant="contained" onClick={go} disabled={busy || !valid} startIcon={busy ? <CircularProgress size={14} color="inherit" /> : undefined}>
          {busy ? "Guardando…" : "Guardar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* ----------------------------- Partners (ABM) ----------------------------- */
function PartnersView() {
  const [partners, setPartners] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [del, setDel] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try { setPartners(await getPartners()); }
    catch (e: any) { setErr(String(e?.message ?? e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const nameOk = /^[A-Za-z0-9]{2,30}$/.test(name.trim());

  async function add() {
    setBusy(true); setErr(null); setOk(null);
    try { setPartners(await addPartner(name.trim())); setOk(`Partner "${name.trim()}" agregado`); setName(""); }
    catch (e: any) { setErr(String(e?.message ?? e)); }
    finally { setBusy(false); }
  }
  async function doDelete() {
    if (!del) return;
    setBusy(true); setErr(null); setOk(null);
    try { setPartners(await removePartner(del)); setOk(`Partner "${del}" eliminado`); setDel(null); }
    catch (e: any) {
      const cols = (e as { collectors?: string[] }).collectors;
      setErr(String(e?.message ?? e) + (cols?.length ? ` — ej: ${cols.slice(0, 8).join(", ")}` : ""));
      setDel(null);
    }
    finally { setBusy(false); }
  }

  return (
    <>
      <Alert severity="info" sx={{ mb: 2 }}>
        Estos son los partners que aparecen en el combo <b>Partner</b> de Configuración. Agregar acá
        actualiza el catálogo (<code>split_to</code>) para que se puedan elegir. No se puede borrar un
        partner que ya esté en uso por algún colector.
      </Alert>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }} sx={{ mb: 2 }}>
        <TextField size="small" label="Nuevo partner" value={name} onChange={(e) => setName(e.target.value)}
          error={name !== "" && !nameOk} helperText="Solo letras y números (2–30)" sx={{ width: 280 }} />
        <Button variant="contained" startIcon={<AddIcon />} disabled={busy || !nameOk} onClick={add} sx={{ height: 40 }}>
          Agregar partner
        </Button>
      </Stack>

      {err && <Alert severity="error" onClose={() => setErr(null)} sx={{ mb: 2 }}>{err}</Alert>}
      {ok && <Alert severity="success" onClose={() => setOk(null)} sx={{ mb: 2 }}>{ok}</Alert>}

      <TableContainer component={Paper} variant="outlined" sx={{ maxWidth: 520 }}>
        <Table size="small">
          <TableHead sx={{ bgcolor: BRAND.primaryLight }}>
            <TableRow><TableCell sx={{ fontWeight: 700 }}>Partner</TableCell><TableCell /></TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={2} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>
            ) : partners.length === 0 ? (
              <TableRow><TableCell colSpan={2} align="center" sx={{ py: 4, color: "text.secondary" }}>Sin partners.</TableCell></TableRow>
            ) : partners.map((p) => (
              <TableRow key={p} hover>
                <TableCell sx={{ fontWeight: 600 }}>{p}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Eliminar (bloqueado si está en uso)"><span>
                    <IconButton size="small" color="error" disabled={busy} onClick={() => setDel(p)}><DeleteIcon fontSize="small" /></IconButton>
                  </span></Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <ConfirmDialog open={!!del} onClose={() => setDel(null)} onConfirm={doDelete} danger
        title="Eliminar partner" okLabel="Eliminar" busyLabel="Eliminando…">
        Se quita <b>{del}</b> del catálogo de partners. Si algún colector lo usa, la baja se rechaza.
      </ConfirmDialog>
    </>
  );
}

/* ---------------------------- Proyección ---------------------------- */
function monthValue(d: Date) { return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; }

function ProjectionView() {
  const now = new Date();
  const [from, setFrom] = useState(monthValue(now));
  const [to, setTo] = useState(monthValue(now));
  const [data, setData] = useState<SplitProjection | null>(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  const run = useCallback(async () => {
    setLoading(true); setErr(null); setOk(null);
    try {
      setData(await getSplitProjection(from.replace("-", ""), to.replace("-", "")));
    } catch (e: any) { setErr(String(e?.message ?? e)); setData(null); }
    finally { setLoading(false); }
  }, [from, to]);
  useEffect(() => { void run(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Reporte CSV por socio comercial (CP-334): se arma en el browser con lo que ya
  // devolvió /splits/projection (incluido `detail` mensual) — sin ir al backend.
  function downloadOne(partner: string) {
    if (!data) return;
    try { downloadPartnerReport(data, partner); setOk(`Descargado ${partnerReportFilename(partner, data.months)}`); }
    catch (e: any) { setErr(String(e?.message ?? e)); }
  }
  async function downloadAll() {
    if (!data) return;
    setDownloading(true); setErr(null);
    try { const n = await downloadAllPartnerReports(data); setOk(`${n} reporte(s) descargado(s), uno por socio`); }
    catch (e: any) { setErr(String(e?.message ?? e)); }
    finally { setDownloading(false); }
  }
  const canDownload = !!data && data.by_partner.length > 0 && !loading && !downloading;

  return (
    <>
      <Alert severity="info" sx={{ mb: 2 }}>
        <b>Estimación.</b> El split no se liquida automáticamente (la config existe pero el core no la aplica).
        Esto es <b>cobros × rate</b> del rango, agrupado por partner — cuánto <i>le correspondería</i> a cada uno.
      </Alert>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }} sx={{ mb: 2 }}>
        <TextField size="small" label="Desde (mes)" type="month" value={from} onChange={(e) => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField size="small" label="Hasta (mes)" type="month" value={to} onChange={(e) => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
        <Button variant="contained" onClick={run} disabled={loading} startIcon={loading ? <CircularProgress size={14} color="inherit" /> : undefined}>
          {loading ? "Calculando…" : "Calcular"}
        </Button>
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Un CSV por socio comercial (el browser puede pedir permiso para varias descargas)">
          <span>
            <Button variant="outlined" onClick={downloadAll} disabled={!canDownload}
              startIcon={downloading ? <CircularProgress size={14} color="inherit" /> : <DownloadIcon />}>
              {downloading ? "Descargando…" : `Descargar todos${data?.by_partner.length ? ` (${data.by_partner.length})` : ""}`}
            </Button>
          </span>
        </Tooltip>
      </Stack>

      {err && <Alert severity="error" onClose={() => setErr(null)} sx={{ mb: 2 }}>{err}</Alert>}
      {ok && <Alert severity="success" onClose={() => setOk(null)} sx={{ mb: 2 }}>{ok}</Alert>}

      {data && (
        <>
          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
            {data.by_partner.length === 0 && <Typography color="text.secondary">Sin colectores con rate en el rango.</Typography>}
            {data.by_partner.map((p) => (
              <Paper key={p.partner} variant="outlined" sx={{ p: 2, minWidth: 220, bgcolor: BRAND.primaryLight }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="caption" sx={{ fontWeight: 700, flex: 1 }}>{p.partner}</Typography>
                  <Tooltip title={`Descargar reporte CSV de ${p.partner} (detalle por colector y mes)`}>
                    <span>
                      <IconButton size="small" onClick={() => downloadOne(p.partner)} disabled={downloading} aria-label={`Descargar reporte de ${p.partner}`}>
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>{fmtMoney(p.projected)}</Typography>
                <Typography variant="caption" color="text.secondary">
                  sobre {fmtMoney(p.cobros)} · {p.collectors} collector(s)
                </Typography>
              </Paper>
            ))}
          </Stack>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead sx={{ bgcolor: BRAND.primaryLight }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Collector</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Partner</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Rate</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Cobros (rango)</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Split estimado</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.rows.length === 0 ? (
                  <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4, color: "text.secondary" }}>Sin datos en el rango.</TableCell></TableRow>
                ) : data.rows.map((r) => (
                  <TableRow key={r.collector} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{r.collector}</TableCell>
                    <TableCell><Chip size="small" label={r.partner} /></TableCell>
                    <TableCell align="right" sx={{ fontFamily: "monospace" }}>{pct(r.rate)}</TableCell>
                    <TableCell align="right">{fmtMoney(r.cobros)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtMoney(r.projected)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Divider />
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", px: 2, py: 1 }}>
              Meses: {data.months.join(", ")} · generado {fmtDateTime(data.generated_at)}
            </Typography>
          </TableContainer>
        </>
      )}
    </>
  );
}
